import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Calculator, DollarSign, TrendingUp, Clock, Users, Building, Briefcase } from 'lucide-react';
import { useCalculations } from '../hooks/useCalculations';
import LeadCaptureModal from './LeadCaptureModal';

const CalculatorForm = () => {
  const [annualSalary, setAnnualSalary] = useState("");
  const [selectedPosition, setSelectedPosition] = useState("");
  const [selectedJobRole, setSelectedJobRole] = useState("");
  const [selectedIndustry, setSelectedIndustry] = useState("");
  const [results, setResults] = useState(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [pendingCalculation, setPendingCalculation] = useState(null);

  const { submitLeadAndCalculation, loading, referenceData } =
    useCalculations();

  const handleCalculate = async () => {
    if (
      !annualSalary ||
      !selectedPosition ||
      !selectedJobRole ||
      !selectedIndustry
    ) {
      alert("Please fill in all fields");
      return;
    }

    // Prepare calculation data
    const calculationData = {
      annualSalary: parseFloat(annualSalary),
      position: selectedPosition,
      jobRole: selectedJobRole,
      industry: selectedIndustry,
    };

    // Store pending calculation and show lead modal
    setPendingCalculation(calculationData);
    setShowLeadModal(true);
  };

  const handleLeadSubmit = async (leadData) => {
    try {
      if (!pendingCalculation) return;

      const result = await submitLeadAndCalculation(
        leadData,
        pendingCalculation,
      );

      // Show results and close modal
      setResults(result.calculation);
      setShowLeadModal(false);
      setPendingCalculation(null);
    } catch (error) {
      console.error("Lead submission failed:", error);
      // Modal stays open for user to retry
    }
  };

  const handleModalClose = () => {
    setShowLeadModal(false);
    setPendingCalculation(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const resetForm = () => {
    setAnnualSalary("");
    setSelectedPosition("");
    setSelectedJobRole("");
    setSelectedIndustry("");
    setResults(null);
  };

  // Use reference data if available, otherwise fallback to defaults
  const jobRoles = referenceData?.job_roles || [
    { value: "accounting-finance", label: "Accounting and Finance" },
    { value: "customer-service", label: "Customer Service" },
    { value: "data-science", label: "Data Science" },
    { value: "human-resources", label: "Human Resources" },
    { value: "marketing", label: "Marketing" },
    { value: "sales", label: "Sales" },
    { value: "software-engineers", label: "Software Engineers" },
    { value: "technology", label: "Technology" },
  ];

  const industries = referenceData?.industries || [
    { value: "technology", label: "Technology & IT" },
    { value: "financial-services", label: "Financial Services" },
    { value: "healthcare", label: "Healthcare & Pharmaceuticals" },
    { value: "manufacturing", label: "Manufacturing & Industrial" },
    { value: "retail-ecommerce", label: "Retail & E-commerce" },
    { value: "education", label: "Education & Training" },
    { value: "government", label: "Government & Public Sector" },
    { value: "telecommunications", label: "Telecommunications" },
    { value: "consulting", label: "Consulting & Professional Services" },
    { value: "real-estate", label: "Real Estate & Construction" },
    { value: "logistics", label: "Logistics & Supply Chain" },
    { value: "hospitality", label: "Hospitality & Tourism" },
    { value: "outsourcing-offshoring", label: "Outsourcing & Offshoring" },
  ];

  const getPositionLabel = (positionValue) => {
    const positions = {
      "entry-level": "Entry Level",
      junior: "Junior Professional",
      "mid-level": "Mid-Level Professional",
      senior: "Senior Professional",
      manager: "Manager",
      "senior-manager": "Senior Manager",
      director: "Director",
      vp: "Vice President",
      "c-level": "C-Level Executive",
    };
    return positions[positionValue] || positionValue;
  };

  // Prepare calculation summary for lead modal
  const calculationSummary = pendingCalculation
    ? {
        positionLabel: getPositionLabel(pendingCalculation.position),
        annualSalary: pendingCalculation.annualSalary,
      }
    : null;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      {/* Lead Capture Modal */}
      <LeadCaptureModal
        isOpen={showLeadModal}
        onClose={handleModalClose}
        onSubmit={handleLeadSubmit}
        isSubmitting={loading}
        calculationSummary={calculationSummary}
      />

      {/* Form Card */}
      <Card className="shadow-lg border-0 bg-gradient-to-br from-slate-50 to-white">
        <CardHeader className="text-center pb-6">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-primary-500 rounded-full">
              <Calculator className="h-6 w-6 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold">
            Employee Attrition Cost Calculator
          </CardTitle>
          <p className=" mt-2">
            Calculate comprehensive replacement costs based on Asia Pacific
            industry data
          </p>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="salary" className="text-sm font-semibold">
                Annual Salary ($)
              </Label>
              <Input
                id="salary"
                type="number"
                placeholder="e.g. 75000"
                value={annualSalary}
                onChange={(e) => setAnnualSalary(e.target.value)}
                className="h-12 border-[#D9D9D9] focus:border-slate-500 transition-colors"
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="position" className="text-sm font-semibold ">
                Position Level
              </Label>
              <Select
                value={selectedPosition}
                onValueChange={setSelectedPosition}
                disabled={loading}
              >
                <SelectTrigger className="h-12 border-[#D9D9D9] focus:border-slate-500">
                  <SelectValue placeholder="Select position level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="entry-level">Entry Level</SelectItem>
                  <SelectItem value="junior">Junior Professional</SelectItem>
                  <SelectItem value="mid-level">
                    Mid-Level Professional
                  </SelectItem>
                  <SelectItem value="senior">Senior Professional</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="senior-manager">Senior Manager</SelectItem>
                  <SelectItem value="director">Director</SelectItem>
                  <SelectItem value="vp">Vice President</SelectItem>
                  <SelectItem value="c-level">C-Level Executive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="jobRole" className="text-sm font-semibold ">
                Job Role
              </Label>
              <Select
                value={selectedJobRole}
                onValueChange={setSelectedJobRole}
                disabled={loading}
              >
                <SelectTrigger className="h-12 border-[#D9D9D9] focus:border-slate-500">
                  <SelectValue placeholder="Select job role" />
                </SelectTrigger>
                <SelectContent>
                  {jobRoles.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="industry" className="text-sm font-semibold ">
                Industry
              </Label>
              <Select
                value={selectedIndustry}
                onValueChange={setSelectedIndustry}
                disabled={loading}
              >
                <SelectTrigger className="h-12 border-[#D9D9D9] focus:border-slate-500">
                  <SelectValue placeholder="Select industry" />
                </SelectTrigger>
                <SelectContent>
                  {industries.map((industry) => (
                    <SelectItem key={industry.value} value={industry.value}>
                      {industry.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-center pt-4">
            <Button
              onClick={handleCalculate}
              disabled={
                loading ||
                !annualSalary ||
                !selectedPosition ||
                !selectedJobRole ||
                !selectedIndustry
              }
              className="px-8 py-3 h-12 bg-[#24202C] hover:bg-[#1D1A24] disabled:bg-[#D3D2D4] text-white font-semibold rounded-lg transition-all duration-200 transform hover:scale-105"
            >
              <div className="flex items-center space-x-2">
                {/* <TrendingUp className="h-4 w-4" /> */}
                <span>Calculate Attrition Cost</span>
              </div>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results Card */}
      {results && (
        <div className="space-y-6 animate-in fade-in-50 duration-500">
          {/* Thank you message */}
          <Card className="shadow-sm border-emerald-200 bg-gradient-to-r from-emerald-50 to-green-50">
            <CardContent className="p-6 text-center">
              <div className="flex justify-center mb-4">
                <div className="p-3 bg-gradient-to-r from-emerald-600 to-green-600 rounded-full">
                  <TrendingUp className="h-6 w-6 text-white" />
                </div>
              </div>
              <h3 className="text-lg font-bold text-emerald-800 mb-2">
                Thank You for Your Interest!
              </h3>
              <p className="text-emerald-700">
                Here's your comprehensive employee attrition cost analysis based
                on Asia Pacific industry data.
              </p>
            </CardContent>
          </Card>

          {/* Summary Stats */}
          <div className="grid md:grid-cols-4 gap-4">
            <Card className="shadow-sm border-slate-200 bg-white">
              <CardContent className="p-4 text-center">
                <div className="flex justify-center mb-2">
                  <Clock className="h-5 w-5 text-slate-600" />
                </div>
                <p className="text-sm text-slate-600 font-medium">
                  Training Days
                </p>
                <p className="text-xl font-bold ">{results.training_days}</p>
              </CardContent>
            </Card>

            <Card className="shadow-sm border-slate-200 bg-white">
              <CardContent className="p-4 text-center">
                <div className="flex justify-center mb-2">
                  <DollarSign className="h-5 w-5 text-slate-600" />
                </div>
                <p className="text-sm text-slate-600 font-medium">Daily Rate</p>
                <p className="text-xl font-bold ">
                  {formatCurrency(results.daily_rate)}
                </p>
              </CardContent>
            </Card>

            <Card className="shadow-sm border-slate-200 bg-white">
              <CardContent className="p-4 text-center">
                <div className="flex justify-center mb-2">
                  <Users className="h-5 w-5 text-slate-600" />
                </div>
                <p className="text-sm text-slate-600 font-medium">
                  Position Multiplier
                </p>
                <p className="text-xl font-bold ">
                  {results.position_multiplier}x
                </p>
              </CardContent>
            </Card>

            <Card className="shadow-sm border-slate-200 bg-white">
              <CardContent className="p-4 text-center">
                <div className="flex justify-center mb-2">
                  <Building className="h-5 w-5 text-slate-600" />
                </div>
                <p className="text-sm text-slate-600 font-medium">
                  Industry Multiplier
                </p>
                <p className="text-xl font-bold ">
                  {results.industry_multiplier}x
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Breakdown */}
          <Card className="shadow-lg border-0 bg-gradient-to-br from-emerald-50 to-green-50 py-5">
            <CardHeader className="text-center pb-4">
              <div className="flex justify-center mb-4">
                <div className="p-3 bg-gradient-to-r from-emerald-600 to-green-600 rounded-full">
                  <Briefcase className="h-6 w-6 text-white" />
                </div>
              </div>
              <CardTitle className="text-xl font-bold ">
                Detailed Cost Breakdown
              </CardTitle>
              <p className="text-sm text-slate-600">
                Based on Asia Pacific industry standards
              </p>
            </CardHeader>

            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-white rounded-lg shadow-sm">
                  <p className="text-sm  font-medium mb-1">Recruitment Costs</p>
                  <p className="text-lg font-bold mt-4">
                    {formatCurrency(results.breakdown.recruitment_costs)}
                  </p>
                  <p className="text-xs text-slate-500">
                    Advertising, screening, interviewing
                  </p>
                </div>

                <div className="p-4 bg-white rounded-lg shadow-sm">
                  <p className="text-sm  font-medium mb-1">
                    Training & Onboarding
                  </p>
                  <p className="text-lg font-bold mt-4">
                    {formatCurrency(
                      results.breakdown.training_onboarding_costs,
                    )}
                  </p>
                  <p className="text-xs text-slate-500">
                    Training programs, materials, mentoring
                  </p>
                </div>

                <div className="p-4 bg-white rounded-lg shadow-sm">
                  <p className="text-sm  font-medium mb-1">Productivity Loss</p>
                  <p className="text-lg font-bold mt-4">
                    {formatCurrency(results.breakdown.productivity_loss)}
                  </p>
                  <p className="text-xs text-slate-500">
                    Lost output during transition
                  </p>
                </div>

                <div className="p-4 bg-white rounded-lg shadow-sm">
                  <p className="text-sm  font-medium mb-1">
                    Administrative Costs
                  </p>
                  <p className="text-lg font-bold mt-4">
                    {formatCurrency(results.breakdown.administrative_costs)}
                  </p>
                  <p className="text-xs text-slate-500">
                    HR processing, documentation
                  </p>
                </div>

                <div className="p-4 bg-white rounded-lg shadow-sm">
                  <p className="text-sm  font-medium mb-1">Benefits Costs</p>
                  <p className="text-lg font-bold mt-4">
                    {formatCurrency(results.breakdown.benefits_cost)}
                  </p>
                  <p className="text-xs text-slate-500">
                    4% of annual salary (APAC average)
                  </p>
                </div>

                <div className="p-4 bg-white rounded-lg shadow-sm">
                  <p className="text-sm  font-medium mb-1">
                    Salary During Training
                  </p>
                  <p className="text-lg font-bold mt-4">
                    {formatCurrency(results.breakdown.salary_during_training)}
                  </p>
                  <p className="text-xs text-slate-500">
                    {results.training_days} days × daily rate
                  </p>
                </div>
              </div>

              <div className="text-center p-6 bg-primary-500 rounded-lg text-white">
                <p className="text-lg font-semibold mb-2">
                  Total Replacement Cost
                </p>
                <p className="text-3xl font-bold">
                  {formatCurrency(results.total_cost)}
                </p>
                <p className="text-sm mt-2 opacity-90">
                  Complete cost to replace this position
                </p>
              </div>
            </CardContent>
          </Card>
          <div className="flex justify-center pt-10">
            <Button
              onClick={resetForm}
              variant="outline"
              // className="px-6 py-2 border-slate-300 hover:bg-slate-50 text-slate-700"
              className="px-8 py-3 h-12 bg-[#24202C] hover:bg-[#1D1A24] hover:text-white disabled:bg-[#D3D2D4] text-white font-semibold rounded-lg transition-all duration-200 transform hover:scale-105"
              disabled={loading}
            >
              Calculate Another
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalculatorForm;